import router from '@/router'
import store from '@/store'

import { MetierWsTpu } from 'modulewebservice'
import { MetierLocalStorage } from 'modulelocalstorage'
import i18n from '../lang/index.js'

import { Operations } from './Operations.js'
import { $alert, $amount } from '@/modules/Utils'
import {ActionEnum, EnumModeCashiers, ServerEnum, ServerSessionUnknown} from '@/modules/Enums'
import { Ticket } from '@/modules/Ticket'

/**
 * Traitement après la récupération d'un numéro de badge
 * Récupération ID badge -> Debit/Credit
 */
export class Scan {
   // Variable compteur de tentative
   // en cas d'erreur de communication avec le serveur
  static counter = 1
  // Variable contenant le mail et nom du porteur pour le ticket
  static carrier = null

  /**
   * Recherche du numéro long à partir d'un numéro court saisi
   * @param {*} number
   * @param {*} critere
   * @param {*} successCallback
   * @param {*} errorCallback
   * @param {*} idNomCarte
   */
  static longnumber (number, critere, idNomCarte, successCallback, errorCallback) {
    store.commit('loader', i18n.t('loader.badge_number_search'))

    MetierWsTpu.callBadgeRecherche(
      MetierLocalStorage.getIdMarchand(),
      MetierLocalStorage.getSession(),
      MetierLocalStorage.getSerial(),
      number,
      critere,
      idNomCarte
    ).then(
      (response) => {
        if (response.OK !== 1) {
          if (response.Msg === ServerSessionUnknown) {
            this.reloadSession(() => {
              this.longnumber(number, critere, idNomCarte, successCallback, errorCallback)
            }, (e) => { errorCallback(e) })
          } else {
            errorCallback(response.Msg)
          }
        } else {
          successCallback(response.Reponses.Reponse)
        }
      },
      (e) => {
        errorCallback(e.message)
      }
    )
  }

  /**
   * Récuprération de l'ID badge
   * @param {*} badgeNumber
   * @param {*} action
   * @param {*} mode
   * @param {*} successCallback
   * @param {*} errorCallback
   * @param pinRequired
   * @param { boolean } recursive
   * @param date
   * @param name
   * @param refTransac
   * @param typeScan
   * @param fromAchat
   * @param idCissCommande
   * @param typeSupport
   * @param idNomCarte
   */
  static solde (badgeNumber, action, mode, successCallback, errorCallback, pinRequired = true, recursive = false, date = null, name = null, refTransac = null, typeScan = 'NFC', fromAchat = false, idCissCommande = null, typeSupport = null, idNomCarte = null) {
    // Reset du carrier
    this.carrier = null

    if (action === ActionEnum.cashlessReset || action === ActionEnum.giftReset) {
      this.launchReset(badgeNumber, successCallback, errorCallback, date, name)
      return
    }
    if (action === ActionEnum.giftCredit) {
      this.badge(badgeNumber, action, mode, successCallback, errorCallback, pinRequired, recursive, date, name, refTransac, false, null, idCissCommande, typeSupport, idNomCarte)
      return
    }

    if (action !== ActionEnum.cashlessDebit && action !== ActionEnum.cashlessCredit && action !== ActionEnum.mobil) {
      this.badge(badgeNumber, action, mode, successCallback, errorCallback, pinRequired, recursive, date, name, refTransac)
      return
    }

    store.commit('loader', i18n.tc('loader.badge_search', this.counter))

    MetierWsTpu.callBadgeSolde(
      MetierLocalStorage.getIdMarchand(),
      MetierLocalStorage.getIdPointDeVente(),
      MetierLocalStorage.getSession(),
      badgeNumber,
      typeScan
    ).then(
      (response) => {
        this.counter = 1
        store.commit('loader', false)

        if (response.OK !== 1) {
          // Si session inconnue -> renouvellement de session avant rappel
          if (response.Msg === ServerSessionUnknown) {
            this.reloadSession(() => {
              this.solde(badgeNumber, action, mode, successCallback, errorCallback, pinRequired, recursive, date, name, refTransac, typeScan, fromAchat)
            }, (e) => { errorCallback(e) })
          // Sinon callBadgeInfos
          // Check for PIN required cashless debit
          } else {
            this.badge(badgeNumber, action, mode, successCallback, errorCallback, pinRequired, recursive, date, name, refTransac, fromAchat, null, idCissCommande)
          }
        } else {
          if (response.Badge && response.Badge.Porteur) {
            this.carrier = response.Badge.Porteur
          }

          let solde = response.Badge.Solde ? response.Badge.Solde : 0
          if (action === ActionEnum.cashlessDebit) {
            // Si solde suffisant
            if (solde >= store.state.amount) {
              // Contrôle demande de PIN
              if (
                response.Badge.minPIN !== undefined &&
                store.state.amount > response.Badge.minPIN &&
                response.Badge.PIN !== undefined &&
                response.Badge.PIN !== ''
              ) {
                errorCallback('pin_required', { code: response.Badge.PIN, type: 'PIN', badge: badgeNumber, infos: response })
              } else {
                // Si pas de PIN demandé -> Lancement débit
                this.launchDebit(response.Badge.IDBadge, mode, badgeNumber, solde, successCallback, errorCallback, recursive, null, null, fromAchat)
              }
            } else {
              errorCallback(i18n.t('error.solde_insufficient_more', { solde: $amount(solde) }))
            }
          } else if (action === ActionEnum.cashlessCredit) {
            // Lancement crédit
            this.launchCredit(response.Badge.IDBadge, mode, badgeNumber, solde, successCallback, errorCallback, recursive, date, name, null, refTransac)
          } else if (action === ActionEnum.mobil) {
            // Récupération des produits de la carte + redirection
            this.getCardProducts(response, errorCallback)
          } else {
            errorCallback(i18n.t('error.no_action'))
          }
        }
      },
      (e) => {
        if (this.counter < 3) {
          this.counter++
          setTimeout(() => {
            this.solde(badgeNumber, action, mode, successCallback, errorCallback, pinRequired, recursive, date, name, refTransac, typeScan, fromAchat)
          }, 1800)
        } else {
          this.counter = 1
          store.commit('loader', false)
          errorCallback(e.message)
        }
      }
    )
  }

  /**
   * Récuprération de l'ID badge
   * @param {*} badgeNumber
   * @param {*} action
   * @param {*} mode
   * @param {*} successCallback
   * @param {*} errorCallback
   * @param {*} pinRequired true by default
   * @param recursive
   * @param date
   * @param name
   * @param refTransac
   * @param fromAchat
   * @param customers
   * @param idCissCommande
   * @param typeSupport
   * @param idNomCarte
   */
  static badge (badgeNumber, action, mode, successCallback, errorCallback, pinRequired = false, recursive = false, date = null, name = null, refTransac = null, fromAchat = false, customers = null, idCissCommande = null, typeSupport = null, idNomCarte = null) {
    store.commit('loader', i18n.tc('loader.badge_search_more', this.counter))
      MetierWsTpu.callBadgeInfos(
        MetierLocalStorage.getIdMarchand(),
        MetierLocalStorage.getSession(),
        MetierLocalStorage.getSerial(),
        badgeNumber
      ).then(
        (response) => {
          this.counter = 1
          store.commit('loader', false)

          if (response.OK === 1) {
            // Sauvegarde du carrier
            if (response.Badge && response.Badge.Porteur) {
              this.carrier = response.Badge.Porteur
              this.carrier.action = action
            } else {
              this.carrier = null
            }

            //Contrôle Solde à 0 pour titres cadeaux
            if (
              action === ActionEnum.gift &&
              response.PME !== undefined && response.PME.Solde === 0
            ) {
              errorCallback(i18n.t('error.gift_card_credit_not_permitted'))
              return
            }

            // Contrôle PIN / CCV
            if (pinRequired) {
              let code = null
              let type = null

              if (
                action === ActionEnum.gift &&
                response.Badge !== undefined && response.Badge.CCV !== undefined && response.Badge.CCV !== ''
              ) {
                code = response.Badge.CCV
                type = 'CCV'
              }

              if (
                action === ActionEnum.cashlessDebit &&
                response.PME !== undefined && response.PME.PIN !== undefined && response.PME.PIN === 1 &&
                response.PME.minPIN !== undefined && store.state.amount > response.PME.minPIN &&
                response.Badge !== undefined && response.Badge.PIN !== undefined && response.Badge.PIN !== ''
              ) {
                code = response.Badge.PIN
                type = 'PIN'
              }

              if (code !== null) {
                errorCallback('pin_required', { code: code, type: type, badge: badgeNumber, infos: response })
              } else {
                this.manageBadge(badgeNumber, action, mode, response, successCallback, errorCallback, recursive, date, name, refTransac, fromAchat, customers, idCissCommande, typeSupport, idNomCarte)
              }
            } else {
              this.manageBadge(badgeNumber, action, mode, response, successCallback, errorCallback, recursive, date, name, refTransac, fromAchat, customers, idCissCommande, typeSupport, idNomCarte)
            }
          } else {
            // Si badge introuvable -> modification du message d'erreur
            if (response.Msg.includes('introuvable')) {
              errorCallback(i18n.t('error.support_unknown'))
              // Si session inconnue -> renouvellement de session avant rappel
            } else if (response.Msg === ServerSessionUnknown) {
              this.reloadSession(() => {
                this.badge(badgeNumber, action, mode, successCallback, errorCallback, pinRequired, recursive, date, name, refTransac, fromAchat, customers)
              }, (e) => { errorCallback(e) })
              // Sinon affichage du message ws
            } else {
              errorCallback(response.Msg)
            }
          }
        },
        (e) => {
          if (this.counter < 3) {
            this.counter++
            setTimeout(() => {
              this.badge(badgeNumber, action, mode, successCallback, errorCallback, pinRequired, recursive, date, name, refTransac, fromAchat, customers)
            }, 1800)
          } else {
            this.counter = 1
            store.commit('loader', false)
            errorCallback(e.message)
          }
        }
      )
  }

  /**
   * Actions suite au badge/infos
   *
   * @param badgeNumber
   * @param {*} action
   * @param {*} mode
   * @param {*} response
   * @param successCallback
   * @param errorCallback
   * @param recursive
   * @param date
   * @param name
   * @param refTransac
   * @param fromAchat
   * @param customers
   * @param idCissCommande
   * @param typeSupport
   * @param idNomCarte
   */
  static manageBadge (badgeNumber, action, mode, response, successCallback, errorCallback, recursive = false, date = null, name = null, refTransac = null, fromAchat = false, customers = null, idCissCommande = null, typeSupport = null, idNomCarte = null) {
    switch (action) {
      case ActionEnum.gift:
        if (response.PME && response.PME.IDTypePorteMonnaie &&
          (response.PME.IDTypePorteMonnaie === ServerEnum.typePorteMonnaieCheque ||
            response.PME.IDTypePorteMonnaie === ServerEnum.typePorteMonnaieCarte ||
            response.PME.IDTypePorteMonnaie === ServerEnum.typePorteMonnaieCashless)
        ) {
          // Chèques cadeaux
          if (response.PME.IDTypePorteMonnaie === ServerEnum.typePorteMonnaieCheque) {
            // CREDIT
            if (response.PME.Solde === 0) {
               errorCallback(i18n.t('error.gift_card_credit_not_permitted'))
            // DEBIT
            } else if (response.PME.Solde === response.PME.Valeur) {
              if (store.getters.chequeDebitAccept) {
                // Débit -> montant = solde
                this.launchDebit(response.IDBadge, mode, badgeNumber, response.PME.Solde, successCallback, errorCallback, recursive, null, response.PME.Solde, false, refTransac)
              } else {
                errorCallback(i18n.t('error.gift_cheque_debit_not_permitted'))
              }
            } else {
              // Erreur -> utilisation classique
              if (response.PME.Solde > response.PME.Valeur) {
                errorCallback(i18n.t('error.gift_cheque_solde_sup'))
              } else {
                errorCallback(i18n.t('error.gift_cheque_solde_ko'))
              }
            }
          }
          // Cartes cadeaux
          else {
            if (response.PME.Solde === 0 && !store.getters.carteCreditAccept) {
              errorCallback(i18n.t('error.gift_card_credit_not_permitted'))
            } else if (response.PME.Solde > 0 && !store.getters.carteDebitAccept) {
              errorCallback(i18n.t('error.gift_card_debit_not_permitted'))
            } else {
              // Contrôle mode crédit et pas de date de fin de validité
              if (response.PME.Solde === 0 && response.PME.DemandePeremption === 1 && date === null) {
                errorCallback('date_required', { badge: badgeNumber, infos: response, defaultDate: response.PME.DatePeremptionDefaut })
              } else {
                const expirationDate = date ?? response.PME.DatePeremptionDefaut
                // Redirection vers saisie montant pour Débit ou Crédit
                router.replace({ name: 'gift-card', params: { idBadge: response.IDBadge, solde: response.PME.Solde, date: expirationDate } })
              }
            }
          }
        } else {
          // Pas un titre cadeaux
          errorCallback(i18n.t('error.gift_not_find'))
        }
        return
      case ActionEnum.cashlessDebit:
        if (response.PME !== undefined) {
          // Si solde suffisant
          if (response.PME.Solde >= store.state.amount) {
            this.launchDebit(response.IDBadge, mode, badgeNumber, response.PME.Solde, successCallback, errorCallback, recursive, null, null, fromAchat, refTransac)
          } else {
            errorCallback(i18n.t('error.solde_insufficient_more', { solde: (response.PME.Solde ? $amount(response.PME.Solde) : $amount(0)) }))
          }
        } else {
          errorCallback(i18n.t('error.cashless_not_pme'))
        }
        return
      case ActionEnum.cashlessCredit:
        if (response.PME !== undefined) {
          // Lancement direct du crédit
          this.launchCredit(response.IDBadge, mode, badgeNumber, response.PME.Solde, successCallback, errorCallback, recursive, date, name,null, refTransac)
        } else {
          errorCallback(i18n.t('error.cashless_not_pme'))
        }
        return
      case ActionEnum.giftCredit:
        if (response.PME !== undefined) {
          if (response.PME.IDTypePorteMonnaie === 8 && !(response.PME.Valeur > 0)) {
            errorCallback(i18n.t('error.value_zero'))
            return
          }
          if (response.PME.Solde === 0) {
            // Lancement direct du crédit
            if (store.state.amount === 0) {
              // On est dans le cadre d'un chèque cadeaux
              store.commit('amount', response.PME.Valeur)
            }

            if (typeSupport === ServerEnum.typePorteMonnaieCheque && store.state.amount !== response.PME.Valeur) {
              errorCallback(i18n.t('error.value_not_permitted'))
              return
            }

            if (typeSupport === response.PME.IDTypePorteMonnaie) {
              // Si on a pas de commande en mode rechargement de titre cadeau, on la crée
              if (idCissCommande === null) {
                this.initCommande(response.IDBadge, mode, badgeNumber, response.PME.Solde, successCallback, errorCallback, date, name, refTransac, response.PME.NumeroCarte, idNomCarte, recursive)
              } else {
                this.launchCredit(response.IDBadge, mode, badgeNumber, response.PME.Solde, successCallback, errorCallback, recursive, date, name, null, refTransac, idCissCommande, response.PME.NumeroCarte)
              }
            } else {
              errorCallback(i18n.t('error.support_not_permitted'))
            }
          } else {
            errorCallback(i18n.t('error.zero_not_permitted'))
          }
        } else {
          errorCallback(i18n.t('error.cashless_not_pme'))
        }
        return
      case ActionEnum.badgeInformations:
        // Redirection vers la vue InfoBadge
        router.replace({ name: 'info-badge', params: { badge: response, carrier: this.carrier, customers: customers } })
        return
      case ActionEnum.clientInformations:
        // Redirection vers la vue ClientEdit
        router.replace({ name: 'customer', params: { customer: this.carrier } })
        return
      case ActionEnum.loyalty:
        // Contrôle carte fidélité
        if (response.Loyalty !== 1) {
          errorCallback(i18n.t('error.loyalty_not_found'))
        } else {
          if (store.getters.checkTransaction && router.history.current.name === 'scan-qrcode') {
            // Si on est en scan QRCode et qu'on doit contrôler le porteur
            router.replace({
              name: 'loyalty-check-carrier',
              params: { idBadge: response.IDBadge, carrier: this.carrier, solde: response.PME.Solde, clientQuestions: response.QuestionsCLI, loyaltyQuestions: response.QuestionsFID }
            })
          } else if (store.getters.postJackpot && response.PME) {
            // Si on est en mode post cagnottage et qu'on a un portemonnaie
            router.replace({
              name: 'loyalty-jackpot',
              params: { idBadge: response.IDBadge, solde: response.PME.Solde, clientQuestions: response.QuestionsCLI, loyaltyQuestions: response.QuestionsFID }
            })
          } else if (response.QuestionsCLI || response.QuestionsFID) {
            // Si il y a des questions
            router.replace({
              name: 'loyalty-questions',
              params: { idBadge: response.IDBadge, clientQuestions: response.QuestionsCLI, loyaltyQuestions: response.QuestionsFID }
            })
          } else {
            // Sinon on lance le calcul de la fidélité
            this.launchFidCalcul(response.IDBadge, errorCallback, null, null, null, null, null, refTransac)
          }
        }
        return
      case ActionEnum.mobil:
        // Récupération des produits de la carte + redirection
        // On converti la réponse pour qu'elle ressemble à celle de badge solde
        response.Badge.IDBadge = response.IDBadge
        this.getCardProducts(response, errorCallback)
        break
      case ActionEnum.cashlessRefill:
        // On retourne l'IDBadge et le PME
        successCallback(response)
        break
      case ActionEnum.voucher:
        router.replace({
          name: 'voucher',
          params: { idBadge: response.IDBadge, voucher: response.LSTVCH, pme: response.PME, carrier: this.carrier }
        })
        break
      default:
        router.replace('/home')
        return
    }
  }

  // ******************************
  // ********** CASHLESS **********
  // ******************************

  /**
   * Paiement / Consommation
   * Débite un porte monnaie electronique
   * @param {*} IDBadge
   * @param {*} mode
   * @param {*} badgeNumber
   * @param solde
   * @param {*} successCallback
   * @param {*} errorCallback
   * @param recursive
   * @param IDAchat
   * @param amount
   * @param fromAchat
   * @param reference
   * @param isCashlessOfflineActif
   */
  static launchDebit (IDBadge, mode, badgeNumber, solde, successCallback, errorCallback, recursive = false, IDAchat = null, amount = null, fromAchat = false, reference = null, isCashlessOfflineActif = false) {
    let ipStart = null
    networkinterface.getWiFiIPAddress((ipInformation) => { ipStart = ipInformation.ip }, null)

    store.commit('loader', i18n.tc('loader.debit', this.counter))

    // Formation de la liste des produits selectionnés
    let products = this.formatProductList()

    // Définition du montant total
    let total = amount !== null ? amount : store.state.amount

    if (!fromAchat) {
      MetierWsTpu.callPmeDebit(
        MetierLocalStorage.getIdMarchand(),
        MetierLocalStorage.getSession(),
        MetierLocalStorage.getSerial(),
        IDBadge,
        total,
        IDAchat,
        MetierLocalStorage.getIdUser(),
        products,
        solde,
        reference
      ).then(
        (response) => {
          this.counter = 1
          store.commit('loader', false)

          if (response.OK === 1) {
            // Si on est en récursif, on retourne d'où on viens
            if (recursive) {
              successCallback({ id: badgeNumber, action: ActionEnum.debit, amount: total, ticket: response.Ticket, carrier: this.carrier })
            } else if (IDAchat !== null) {
              successCallback(response.Ticket)
            } else if (isCashlessOfflineActif) {
              successCallback(response)
            } else {
              // Sinon affichage du ticket
              successCallback(null)
              router.replace({ name: 'ticket', params: { tickets: [response.Ticket], carrier: this.carrier,  idBadge: IDBadge } })
            }
          } else {
            if (response.Msg === 'Solde incohérent') {
              this.getDuplicate(IDBadge)
            } else {
              errorCallback(response.Msg)
            }
          }
        },
        () => {
          this.catchTransactionErreur(ipStart, ActionEnum.debit, IDBadge, mode, badgeNumber, solde, successCallback, errorCallback, recursive, IDAchat, amount, null, null, reference, fromAchat)
        }
      )
    } else {
      MetierWsTpu.callPmeAchat(
        MetierLocalStorage.getIdMarchand(),
        MetierLocalStorage.getSession(),
        MetierLocalStorage.getSerial(),
        IDBadge,
        total,
        MetierLocalStorage.getIdUser(),
        products,
        solde,
        reference
      ).then(
        (response) => {
          this.counter = 1
          store.commit('loader', false)

          if (response.OK === 1) {
            // Si on est en récursif, on retourne d'où l'on vient
            if (recursive) {
              successCallback({ id: badgeNumber, action: ActionEnum.debit, amount: total, ticket: response.Ticket, carrier: this.carrier })
            } else if (IDAchat !== null) {
              successCallback(response.Ticket)
            } else {
              // Sinon affichage du ticket
              successCallback(null)
              router.replace({ name: 'ticket', params: { tickets: [response.Ticket], carrier: this.carrier, idBadge: IDBadge } })
            }
          } else {
            if (response.Msg === 'Solde incohérent') {
              this.getDuplicate(IDBadge)
            } else {
              errorCallback(response.Msg)
            }
          }
        },
        () => {
          this.catchTransactionErreur(ipStart, ActionEnum.debit, IDBadge, mode, badgeNumber, solde, successCallback, errorCallback, recursive, IDAchat, amount, null, null, reference, fromAchat)
        }
      )
    }
  }

  /**
   * Rechargement / Activation
   * Crédite un porte monnaie electronique
   * @param {*} IDBadge
   * @param {*} mode
   * @param {*} badgeNumber
   * @param {*} solde
   * @param {*} successCallback
   * @param {*} errorCallback
   * @param recursive
   * @param date | nullable
   * @param name | nullable
   * @param amount | nullable
   * @param refTransac | nullable
   * @param idCissCommande | nullable
   * @param numeroCarte | nullable
   */
  static launchCredit (IDBadge, mode, badgeNumber, solde, successCallback, errorCallback, recursive = false, date = null, name = null, amount = null, refTransac = null, idCissCommande = null, numeroCarte = null) {
    let ipStart = null
    networkinterface.getWiFiIPAddress((ipInformation) => { ipStart = ipInformation.ip }, null)

    store.commit('loader', i18n.tc('loader.credit', this.counter))

    // Définition du montant total
    let total = amount !== null ? amount : store.state.amount
    MetierWsTpu.callPmeCredit(
      MetierLocalStorage.getIdMarchand(),
      MetierLocalStorage.getSession(),
      MetierLocalStorage.getSerial(),
      IDBadge,
      total,
      mode,
      MetierLocalStorage.getIdUser(),
      refTransac,
      date,
      name,
      solde,
      [],
      idCissCommande
    ).then(
      (response) => {
        this.counter = 1
        let action = ActionEnum.credit
        store.commit('loader', false)
        response.NumeroCarte = numeroCarte
        if (response.OK !== 1) {
          if (response.Msg === 'Solde incohérent') {
            this.getDuplicate(IDBadge)
          } else {
            errorCallback(response.Msg)
          }
        } else {
          // Si on est en récursif, on retourne d'où on viens
          if (idCissCommande !== null) {//on est dans le crédit par lot
            action = ActionEnum.giftCredit
          }
          if (recursive) {
            successCallback({ id: badgeNumber, action: action, amount: total, ticket: response.Ticket, carrier: this.carrier, idCissCommande: idCissCommande , NumeroCarte: response.NumeroCarte })
          } else {
            successCallback(null)
            router.replace({ name: 'ticket', params: { tickets: [response.Ticket].concat(Ticket.bankingTickets), carrier: this.carrier } })
          }
        }
      },
      () => {
        let action = ActionEnum.credit
        if (idCissCommande !== null) {
          action = ActionEnum.giftCredit
        }
        this.catchTransactionErreur(ipStart, action, IDBadge, mode, badgeNumber, solde, successCallback, errorCallback, recursive, null, null, date, name, refTransac, false, idCissCommande, numeroCarte)
      }
    )
  }

  /**
   * Catch d'un transaction en erreur (timeout)
   * @param ipStart
   * @param {*} type
   * @param {*} IDBadge
   * @param {*} mode
   * @param {*} badgeNumber
   * @param {*} solde
   * @param {*} successCallback
   * @param {*} errorCallback
   * @param {*} recursive
   * @param {*} IDAchat
   * @param {*} amount
   * @param {*} date
   * @param {*} name
   * @param {*} refTransac
   * @param fromAchat
   * @param idCissCommande | nullable
   * @param numeroCarte | nullable
   */
  static catchTransactionErreur (ipStart, type, IDBadge, mode, badgeNumber, solde, successCallback, errorCallback, recursive = null, IDAchat = null, amount = null, date = null, name = null, refTransac = null, fromAchat = false, idCissCommande = null, numeroCarte = null) {
    store.commit('loader', false)
    // Récupération IP après transac
    networkinterface.getWiFiIPAddress((ipInformation) => {
      if (ipStart !== ipInformation.ip) {
        // Sinon différent -> alerte erreur
        this.counter = 1
        store.commit('loader', false)
        $alert(
          i18n.t('error.network_change'),
          'info',
          i18n.t('error.check_duplicata'),
          -1,
          [{
            title: i18n.t('error.display_duplicata'),
            color: 'info',
            click: () => { this.getDuplicate(IDBadge) }
          }]
        )
      } else {
        // Proposition nouvelle tentative
        if (this.counter < 3) {
          $alert(
            i18n.t('error.long_trx'),
            'info',
            i18n.t('error.retry_counter', { counter: (this.counter + 1) }),
            -1,
            [{
              title: i18n.t('error.retry', { counter: (this.counter + 1) }),
              color: 'info',
              click: () => {
                this.counter++
                if (type === ActionEnum.debit) {
                  this.launchDebit(IDBadge, mode, badgeNumber, solde, successCallback, errorCallback, recursive, IDAchat, amount, fromAchat, refTransac)
                } else {
                  this.launchCredit(IDBadge, mode, badgeNumber, solde, successCallback, errorCallback, recursive, date, name, amount, refTransac, idCissCommande, numeroCarte)
                }
              }
            }, {
              title: i18n.t('error.check_infos'),
              color: 'success',
              click: () => {
                this.counter = 1
                store.commit('amount', 0)
                this.badge(badgeNumber, ActionEnum.badgeInformations, '', () => {}, () => {
                  $alert(i18n.t('error.search_infos'))
                  router.replace('/home')
                })
              }
            }]
          )
        } else {
          this.counter = 1
          errorCallback(i18n.t('error.transaction'))
        }
      }
    }, null)
  }

  /**
   * Effectue un badge/duplicata
   * @param {*} IDBadge
   */
  static getDuplicate (IDBadge) {
    Operations.badgeDuplicate(
      IDBadge,
      this.carrier,
      (msg) => {
        $alert(msg, 'info')
      },
      () => {
        $alert(i18n.t('error.operation'))
        router.replace('/home')
      }
    )
  }

  /**
   * Lancer un transfert de solde d'un badge à un autre
   * @param { String } idBadge L'id du badge sur lequel on veut prendre l'argent
   * @param { String } cardNum Le numéro (long ou nfc) de la sur laquelle on veut envoyer l'argent
   * @param { Number } amount Le montant du transfert en centimes
   * @param { String } reference Reference de la transaction
   */
  static transferSolde (idBadge, cardNum, amount, reference) {
    store.commit('loader', i18n.t('loader.tranfert'))

    return new Promise(((resolve, reject) => {
      MetierWsTpu.callPmeTransfert(
        MetierLocalStorage.getIdMarchand(),
        MetierLocalStorage.getSession(),
        MetierLocalStorage.getSerial(),
        idBadge,
        cardNum,
        amount,
        reference
      ).then(
        (response) => {
          store.commit('loader', false)
          if (response.OK !== 1) {
            reject(response.Msg)
          } else {
            router.replace({ name: 'ticket', params: { tickets: [response.Ticket], carrier: this.carrier } })
            resolve(response)
          }
        },
        (e) => {
          store.commit('loader', false)
          reject(e.message)
        }
      )
    }))
  }

  /**
   * Reset d'un badge cashless
   * @param {*} numBadge
   * @param {*} successCallback
   * @param {*} errorCallback
   * @param {*} date
   * @param {*} name
   */
  static launchReset (numBadge, successCallback, errorCallback, date, name) {
    store.commit('loader', i18n.t('loader.badge_reinit'))

    MetierWsTpu.callPmeReset(
      MetierLocalStorage.getIdMarchand(),
      MetierLocalStorage.getSession(),
      MetierLocalStorage.getSerial(),
      numBadge
    ).then(
      (response) => {
        store.commit('loader', false)

        if (response.OK !== 1) {
          if (response.Msg === ServerSessionUnknown) {
            this.reloadSession(() => {
              this.launchReset(numBadge, successCallback, errorCallback, date, name)
            }, (e) => { errorCallback(e) })
          } else {
            errorCallback(response.Msg)
          }
        } else {
          if (store.state.amount > 0) {
            this.launchCredit(response.IDBadge, 'P', numBadge, null, successCallback, errorCallback, true, date, name)
          } else {
            successCallback({ id: numBadge, action: ActionEnum.reset })
          }
        }
      },
      (e) => {
        store.commit('loader', false)
        errorCallback(e.message)
      }
    )
  }

  /**
   * @param {*} IDBadge
   * @param {*} mode
   * @param {*} badgeNumber
   * @param {*} solde
   * @param {*} successCallback
   * @param {*} errorCallback
   * @param date
   * @param name
   * @param refTransac
   * @param numeroCarte
   * @param idNomCarte
   * @param recursive
   */
  static initCommande (IDBadge, mode, badgeNumber, solde, successCallback, errorCallback, date, name, refTransac, numeroCarte, idNomCarte, recursive = false) {
    store.commit('loader', i18n.t('loader.command'))

    MetierWsTpu.callInitCommande(
      MetierLocalStorage.getIdMarchand(),
      MetierLocalStorage.getSession(),
      date,
      store.state.amount,
      name,
      idNomCarte
    )
      .then(
        (response) => {
          store.commit('loader', false)

          if (response.OK === 1 && response.Commande.IDCissCommande > 0) {
            this.launchCredit(IDBadge, mode, badgeNumber, solde, successCallback, errorCallback, recursive, date, name, null, refTransac, response.Commande.IDCissCommande, numeroCarte)
          } else {
            errorCallback(response.Msg)
          }
        },
        (e) => {
          this.$store.commit('loader', false)
          $alert(e)
        }
      )
  }

  // **************************************
  // ********** CASHLESS OFFLINE **********
  // **************************************

  /**
   * Récupère le solde du compte en ligne à partir de la carte offline
   * @param uid
   * @param offlineInfos
   * @param solde
   * @param amount
   */
  static checkRefill (uid, offlineInfos = null, solde = null, amount = null) {
    Scan.badge(
      uid,
      ActionEnum.cashlessRefill,
      'P',
      (response) => {
        if (response.ComptePME && response.ComptePME.Solde > 0) {
          if (solde && amount && (solde + response.ComptePME.Solde) < amount) {
            $alert(i18n.t('error.online_solde_insufficient'))
          } else {
            // Redirection vers la vue de choix du montant à recharger
            router.replace({
              name: 'cashless-refill',
              params: { offlineInfos: offlineInfos, offlineSolde: solde, onlineInfos: response.ComptePME }
            })
          }
        } else if (response.PME && !response.PME.Offline && response.PME.Solde > 0) {
          // Redirection vers la vue de choix du montant à recharger
          router.replace({
            name: 'cashless-refill',
            params: { offlineInfos: offlineInfos, onlineInfos: response.PME }
          })
        } else {
          $alert(i18n.t('error.solde_insufficient'), 'warning')
        }
      },
      (error) => {
        // Affichage erreur
        $alert(i18n.t('error.solde_insufficient'), 'warning', error)
      }
    )
  }

  /**
   * Rechargement / Activation
   * Crédite un porte monnaie electronique
   * @param userdata
   * @param {*} successCallback
   * @param {*} errorCallback
   */
  static debitOnline (userdata, successCallback, errorCallback) {
    store.commit('loader', i18n.t('loader.reload_search'))

    let env = MetierLocalStorage.getEnv()

    // Montant max lors du débit du pme online pour les environnements de dev et int
    let amountMax = null
    if (env !== null && env > 0) {
      amountMax = 1000
    }

    MetierWsTpu.callDebitOnline(
      MetierLocalStorage.getIdMarchand(),
      MetierLocalStorage.getSession(),
      MetierLocalStorage.getSerial(),
      store.getters.idEvent,
      userdata,
      amountMax
    ).then(
      (response) => {
        store.commit('loader', false)

        if (response.OK === 1) {
          successCallback(response)
        } else {
          errorCallback(response.Msg)
        }
      },
      (e) => {
        store.commit('loader', false)
        errorCallback(e.message)
      }
    )
  }

  /**
   * Annulation débit online (refill offline)
   * @param {*} idBadge
   * @param {*} successcallback
   * @param {*} errorcallback
   */
  static cancelDebitOnline (idBadge, successcallback, errorcallback) {
    store.commit('loader', i18n.t('loader.reload_cancellation'))

    MetierWsTpu.callBadgeAnnulation(
      MetierLocalStorage.getIdMarchand(),
      MetierLocalStorage.getSession(),
      MetierLocalStorage.getSerial(),
      idBadge
    ).then(
      (response) => {
        store.commit('loader', false)
        if (response.OK === 1) {
          successcallback()
        } else {
          errorcallback(response.Msg)
        }
      },
      (e) => {
        store.commit('loader', false)
        errorcallback(e.message)
      }
    )
  }

  // ******************************
  // ********** FIDELITE **********
  // ******************************

  /**
   * Calcul de la fidélité d'une carte
   * @param {*} IDBadge
   * @param {*} errorCallback
   * @param clientAnswers
   * @param loyaltyAnswers
   * @param amount
   * @param postJackpotAmount
   * @param solde
   * @param reference
   */
  static launchFidCalcul (IDBadge, errorCallback, clientAnswers = null, loyaltyAnswers = null, amount = null, postJackpotAmount = null, solde = null, reference = null) {
    store.commit('loader', i18n.t('loader.loyalty'))
    const total = amount !== null ? amount : store.state.amount
    let cashierID = MetierLocalStorage.getConnectedCashier() ? MetierLocalStorage.getConnectedCashier().id : null
    MetierWsTpu.callFidCalcul(
      MetierLocalStorage.getIdMarchand(),
      MetierLocalStorage.getSession(),
      MetierLocalStorage.getSerial(),
      IDBadge,
      total,
      clientAnswers,
      loyaltyAnswers,
      total - postJackpotAmount,
      reference,
      cashierID
    ).then(
      (response) => {
        store.commit('loader', false)
        let floatingModeEnabled = store.state.teleparameters.CashierEngagementMode === EnumModeCashiers.FLOATING
        if (response.OK === 1) {
          // Mode Béssé
          if (store.getters.postJackpot) {
            if (postJackpotAmount > 0) {
              this.launchDebit(
                IDBadge,
                'P',
                '',
                solde,
                (ticket) => {
                  router.replace({
                    name: 'ticket',
                    params: { tickets: [response.Ticket, ticket], carrier: this.carrier, leftToPay: total - postJackpotAmount, floatingModeEnabled: floatingModeEnabled }
                  })
                },
                errorCallback,
                false,
                response.IDAchat,
                postJackpotAmount,
                false,
                reference
              )
            } else {
              router.replace({
                name: 'ticket',
                params: { tickets: [response.Ticket], carrier: this.carrier, leftToPay: total, floatingModeEnabled: floatingModeEnabled } })
            }
          } else if (response.Cadeaux !== undefined && response.Cadeaux.Nb > 0) {
            router.replace({
              name: 'loyalty-gift',
              params: { idBadge: IDBadge, idAchat: response.IDAchat, ticket: response.Ticket, gifts: response.Cadeaux.Cadeau, multiSelection: response.Cadeaux.ChoixMultipleAutorise === 1, carrier: this.carrier }
            })
          } else if (response.UsagePME === 1 && response.SoldePME > 0) {
            router.replace({
              name: 'loyalty-jackpot',
              params: { idBadge: IDBadge, idAchat: response.IDAchat, solde: response.SoldePME, ticket: response.Ticket, carrier: this.carrier }
            })
          } else {
            router.replace({ name: 'ticket', params: { tickets: [response.Ticket], carrier: this.carrier, leftToPay: total, floatingModeEnabled: floatingModeEnabled } })
          }
        } else {
          errorCallback(response.Msg)
        }
      },
      (e) => {
        store.commit('loader', false)
        errorCallback(e.message)
      }
    )
  }

  /**
   * Consommation d'un cadeau de fidélité
   * @param {*} idBadge
   * @param {*} idBuy
   * @param {*} idGift
   * @param {*} fidTicket
   * @param {*} amount
   * @param {*} giftArray
   */
  static launchFidConso (idBadge, idBuy, fidTicket, idGift = null, amount = null, giftArray = null) {
    store.commit('loader', i18n.t('loader.loyalty_conso'))
    let cashierID = MetierLocalStorage.getConnectedCashier() ? MetierLocalStorage.getConnectedCashier().id : null
    MetierWsTpu.callFidConsommation(
      MetierLocalStorage.getIdMarchand(),
      MetierLocalStorage.getSession(),
      MetierLocalStorage.getSerial(),
      idBadge,
      idBuy,
      idGift,
      amount,
      giftArray,
      cashierID
    ).then(
      (response) => {
        store.commit('loader', false)

        if (response.OK === 1) {
          router.replace({
            name: 'ticket',
            params: {
              tickets: [fidTicket, response.Ticket],
              leftToPay: response.ResteAPayer,
              carrier: this.carrier,
              floatingModeEnabled :store.state.teleparameters.CashierEngagementMode ?  store.state.teleparameters.CashierEngagementMode ===  EnumModeCashiers.FLOATING : null
            }
          })
        } else {
          $alert(response.Msg)
        }
      },
      (e) => {
        store.commit('loader', false)
        $alert(e.message)
      }
    )
  }

  /**
   * Consommation de bons d'achats
   * @param {*} idBadge
   * @param {*} idVch
   * @param {*} nbVch
   * @param {*} successCallback
   * @param {*} errorCallback
   */
  static launchVoucherConso (idBadge, idVch, nbVch, successCallback, errorCallback) {
    store.commit('loader', i18n.t('loader.voucher_conso'))

    MetierWsTpu.callVoucherConsommation(
      MetierLocalStorage.getIdMarchand(),
      MetierLocalStorage.getSession(),
      MetierLocalStorage.getSerial(),
      idBadge,
      idVch,
      nbVch
    ).then(
      (response) => {
        store.commit('loader', false)

        if (response.OK === 1) {
          successCallback(response.Ticket)
        } else {
          errorCallback(response.Msg)
        }
      },
      (e) => {
        store.commit('loader', false)
        errorCallback(e.message)
      }
    )
  }

  // ***************************
  // ********** MOBIL **********
  // ***************************

  /**
   * Récupère les produits de la carte scannée
   * @param badgeInfosResponse
   * @param errorCallback
   */
  static getCardProducts (badgeInfosResponse, errorCallback) {
    store.commit('loader', i18n.t('loader.mobil_scan'))

    MetierWsTpu.callCatalogueCarteProduits(
      MetierLocalStorage.getIdMarchand(),
      MetierLocalStorage.getSession().session,
      badgeInfosResponse.Badge.IDBadge
    ).then(
      (response) => {
        store.commit('loader', false)

        if (response.OK === 1) {
          router.replace({
            name: 'mobil-products',
            params: { badge: badgeInfosResponse.Badge, pme: badgeInfosResponse.PME, productList: response.produits }
          })
        } else {
          errorCallback(response.Msg)
        }
      },
      (e) => {
        store.commit('loader', false)
        errorCallback(e.message)
      }
    )
  }

  /**
   * Effectue un débit avec réponse (type Mobil)
   * @param idBadge
   * @param products
   * @param errorCallback
   * @param km
   * @param reference
   */
  static launchPmeDebitReponses (idBadge, products, errorCallback, km = null, reference = null) {
    store.commit('loader', i18n.t('loader.mobil_payment'))

    let reponses = null
    if (km) {
      let question = store.getters.questionsMobil[0]

      if (question) {
        reponses = [
          {
            IDQuestion: question.IDQuestion,
            IDReponsePossible: question.reponsespossible[0].IDReponsePossible,
            Reponse: km
          }
        ]
      }
    }

    MetierWsTpu.callPmeDebitReponses(
      MetierLocalStorage.getIdMarchand(),
      MetierLocalStorage.getSession(),
      MetierLocalStorage.getSerial(),
      idBadge,
      store.state.amount,
      this.formatProductListMobil(products),
      reponses,
      reference
    ).then(
      (response) => {
        store.commit('loader', false)

        if (response.OK === 1) {
          router.replace({ name: 'ticket', params: { tickets: [response.Ticket], carrier: this.carrier } })
        } else if (response.Msg?.startsWith('DOU_0001:')) {
          const buttons = [{
            title: i18n.t('button.display_ticket'),
            color: 'info',
            click: () => {
              if (response.Ticket) {
                router.replace({ name: 'ticket', params: { tickets: [response.Ticket], carrier: this.carrier } })
              } else {
                this.getDuplicate(idBadge)
              }
            }
          }, {
            title: i18n.t('button.cancel'),
            color: 'error',
            click: () => {
              errorCallback(null)
            }
          }]

          $alert(
            i18n.t('error.duplicate_transaction'),
            'warning',
            '',
            -1,
            buttons,
            null,
            false
          )
        } else {
          errorCallback(response.Msg)
        }
      },
      (e) => {
        store.commit('loader', false)
        errorCallback(e.message)
      }
    )
  }

  static launchEditionMobil (session, products, successCallback, errorCallback) {
    store.commit('loader', i18n.t('loader.mobil_edition'))

    MetierWsTpu.callModificationTransaction(
      session,
      store.state.amount,
      this.formatProductListMobil(products),
    ).then(
      (response) => {
        store.commit('loader', false)

        if (response.OK === 1) {
          successCallback()
        } else {
          errorCallback(response.Msg)
        }
      },
      (e) => {
        store.commit('loader', false)
        errorCallback(e.message)
      }
    )
  }

  static formatProductListMobil (products) {
    let purchases = []
    products.forEach(element => {
      if (element.quantity > 0) {
        purchases.push({
          IDArticle: element.id,
          Quantite: element.quantity,
          MontantTotal: element.total
        })
      }
    })
    return purchases
  }

  // ********************************
  // ********** RESTOFLASH **********
  // ********************************

  /**
   * Effectue un paiement RestoFlash
   *
   * @param {*} code code donnée par le client
   * @param errorCallback
   */
  static restoflashPayment (code, errorCallback) {
    store.commit('loader', i18n.t('loader.restoflash_payment'))

    MetierWsTpu.callRestoFlashPayment(
      MetierLocalStorage.getIdMarchand(),
      MetierLocalStorage.getSession(),
      MetierLocalStorage.getSerial(),
      code
    ).then(
      (response) => {
        store.commit('loader', false)

        if (response.OK === 1) {
          // TODO Formatter le ticket provenant de RestoFlash
          router.replace({ name: 'ticket', params: { tickets: [response.Ticket] } })
        } else {
          errorCallback(response.Msg)
        }
      },
      (e) => {
        store.commit('loader', false)
        errorCallback(e.message)
      }
    )
  }

  // *******************************************************************
  // *******************************************************************

  /**
   * Renouvellement de la session
   * @param {*} callbackSuccess
   * @param {*} callbackError
   */
  static reloadSession (callbackSuccess, callbackError) {
    store.commit('loader', i18n.t('loader.session_reload'))

    MetierWsTpu.callInitSession(
      MetierLocalStorage.getIdMarchand(),
      MetierLocalStorage.getApiKey()
    ).then(
      (response) => {
        if (response.OK === 1) {
          MetierLocalStorage.setSession(response.Session)

          // Renouvellement de la session du plugin cashless
          if (IS_ANDROID) {
            Cashless.updateSession({ session: response.Session })
          }

          callbackSuccess(true)
        } else {
          store.commit('loader', false)
          callbackError(response.Msg)
        }
      },
      (e) => {
        store.commit('loader', false)
        callbackError(e.message)
      }
    )
  }

  /**
   * Formation de la liste des produits selectionnés
   */
  static formatProductList () {
    let products = []

    // Création de la liste d'articles (débit)
    store.state.listproducts.forEach(list => {
      list.products.forEach(product => {
        if (product !== null && product.quantity > 0) {
          products.push({
            'IDArticle': product.id,
            'TypeArticle': 0,
            'Quantite': product.quantity,
            'PrixUnitaire': product.price
          })
        }
      })
    })

    // Ajout consignes
    store.state.consignes.forEach(el => {
      if (el.consigne.number > 0) {
        products.push({
          'IDArticle': el.consigne.IDArticle,
          'TypeArticle': 1,
          'Quantite': el.consigne.number,
          'PrixUnitaire': el.consigne.PrixUnitaireTTC
        })
      }

      if (el.deconsigne.number > 0) {
        products.push({
          'IDArticle': el.deconsigne.IDArticle,
          'TypeArticle': 2,
          'Quantite': el.deconsigne.number,
          'PrixUnitaire': el.deconsigne.PrixUnitaireTTC
        })
      }
    })

    // // Ajout consigne ECOCUP
    // if (store.state.ecocup.consigne > 0) {
    //   products.push({
    //     'IDArticle': store.state.ecocup.idConsigne,
    //     'Quantite': store.state.ecocup.consigne,
    //     'PrixUnitaire': store.state.ecocup.price
    //   })
    // }
    // // Ajout déconsigne ECOCUP
    // if (store.state.ecocup.deconsigne > 0) {
    //   products.push({
    //     'IDArticle': store.state.ecocup.idDeconsigne,
    //     'Quantite': store.state.ecocup.deconsigne,
    //     'PrixUnitaire': -(store.state.ecocup.price)
    //   })
    // }

    return products
  }

  /**
   * Formation d'un ticket en mode offline
   */
  static formatTicket (type, amount, oldSolde, newSolde, frais = 0, code = null) {
    let oldSoldeFormatted = $amount(oldSolde >= 0 ? oldSolde : 0)
    let amountFormatted = $amount(amount)
    let newSoldeFormatted = $amount(newSolde)
    let fraisFormatted = frais === 0 ? null : $amount(frais)
    let site = MetierLocalStorage.getSiteWeb()

    let ticket = {
      Lignes: [
        { Msg: type + ' CASHLESS', Gras: 0, Souligne: 1, Align: 'C', Taille: 2 },
        { Msg: this.getFormattedDate(), Gras: 0, Souligne: 0, Align: 'L', Taille: 2 },
        { Msg: MetierLocalStorage.getNomMarchand().toUpperCase(), Gras: 0, Souligne: 0, Align: 'L', Taille: 2 },
        { Msg: i18n.t('tickets.old_solde') + ': ' + oldSoldeFormatted, Gras: 0, Souligne: 0, Align: 'L', Taille: 2 },
        { Msg: type + ' : ', Gras: 0, Souligne: 0, Align: 'L', Taille: 2 },
        { Msg: amountFormatted, Gras: 1, Souligne: 0, Align: 'R', Taille: 2 }
      ]
    }

    if (fraisFormatted !== null) {
      ticket.Lignes.push({ Msg: i18n.t('tickets.activation_fees') + ': ' + fraisFormatted, Gras: 0, Souligne: 0, Align: 'L', Taille: 2 })
    }

    ticket.Lignes.push({ Msg: i18n.t('tickets.new_solde') + ': ' + newSoldeFormatted, Gras: 0, Souligne: 0, Align: 'L', Taille: 2 })

    if (code !== null) {
      ticket.Lignes.push({ Msg: '--------------------', Gras: 0, Souligne: 0, Align: 'C', Taille: 2 })

      if (site !== null) {
        ticket.Lignes.push(
          { Msg: i18n.t('tickets.follow_us'), Gras: 0, Souligne: 0, Align: 'L', Taille: 2 },
          { Msg: site, Gras: 0, Souligne: 0, Align: 'R', Taille: 2 }
        )
      }

      ticket.Lignes.push(
        { Msg: i18n.t('tickets.add_code'), Gras: 0, Souligne: 0, Align: 'L', Taille: 2 },
        { Msg: code, Gras: 1, Souligne: 0, Align: 'R', Taille: 2 },
        { Msg: '--------------------', Gras: 0, Souligne: 0, Align: 'C', Taille: 2 }
      )
    }

    ticket.Lignes.push({ Msg: i18n.t('tickets.thx'), Gras: 0, Souligne: 0, Align: 'C', Taille: 2 })

    return ticket
  }

  static getFormattedDate () {
    let d = new Date()
    return 'LE ' +
      [d.getDate().padLeft(), (d.getMonth() + 1).padLeft(), d.getFullYear()].join('/') +
      ' A ' +
      [d.getHours().padLeft(), d.getMinutes().padLeft(), d.getSeconds().padLeft()].join(':')
  }

  static bytesToHexString (bytes) {
    let dec = ''
    let hexstring = ''
    let bytesAsHexString = ''
    // Inversion pour les carte de type NfcV
    let invert = bytes[bytes.length - 1] === -32

    for (let i = 0; i < bytes.length; i++) {
      if (bytes[i] >= 0) {
        dec = bytes[i]
      } else {
        dec = 256 + bytes[i]
      }

      hexstring = dec.toString(16)
      // zero padding
      if (hexstring.length === 1) {
        hexstring = '0' + hexstring
      }

      if (invert) {
        bytesAsHexString = hexstring + bytesAsHexString
      } else {
        bytesAsHexString += hexstring
      }
    }
    return bytesAsHexString
  }

  // *******************************************************************
  // *******************************************************************

  /**
   * Changement statut de la commande
   * @param {*} callbackSuccess
   * @param {*} callbackError
   * @param idCommande
   * @param StatutPreparation
   */
  static changeStatut (idCommande, StatutPreparation,  callbackSuccess, callbackError) {
    store.commit('loader', i18n.t('loader.change_statut'))
    // console.log('chanestatut', StatutPreparation)
    MetierWsTpu.callSetStatutCommande(
      MetierLocalStorage.getIdMarchand(),
      MetierLocalStorage.getSession(),
      MetierLocalStorage.getSerial(),
      idCommande,
      StatutPreparation
    ).then(
      (response) => {
        if (response.OK === 1) {
          store.commit('loader', false)
          callbackSuccess(true)
        } else {
          store.commit('loader', false)
          callbackError(response.Msg)
        }
      },
      (e) => {
        store.commit('loader', false)
        callbackError(e.message)
      }
    )
  }

  /**
   * Récupérer des informations de commande
   * @param badgeNumber
   * @param successCallback
   * @param errorCallback
   */
  static getCommandeInfos (badgeNumber, successCallback, errorCallback) {
    store.commit('loader', i18n.tc('loader.badge_search_more', this.counter))
      MetierWsTpu.callCommandeInfos(
        MetierLocalStorage.getIdMarchand(),
        MetierLocalStorage.getSession(),
        MetierLocalStorage.getSerial(),
        badgeNumber
      ).then(
        (response) => {
          this.counter = 1
          store.commit('loader', false)

          if (response.OK === 1 && response.commande) {
            router.replace({ name: 'info-commande', params: { badge: response.commande } })
          } else {
            errorCallback(response.Msg ?? 'Merci de vérifier votre QR code')
          }
        },
        (e) => {
          if (this.counter < 3) {
            this.counter++
            setTimeout(() => {
              this.getCommandeInfos(badgeNumber, successCallback, errorCallback)
            }, 1800)
          } else {
            this.counter = 1
            store.commit('loader', false)
            errorCallback(e.message)
          }
        }
      )
  }
}
